import { create } from "zustand";
import { persist } from "zustand/middleware";

export type DealsView = "table" | "kanban";

/**
 * A field id from `components/deals/fields.ts`.
 *
 * Deliberately a plain string rather than a union: the registry decides what is
 * sortable, and a closed union here would have to be edited every time a field
 * is added — which was the original problem. `compareDeals` returns 0 for an id
 * it cannot resolve, so an unknown value degrades to "unsorted" rather than
 * throwing.
 */
export type SortField = string;

/** Sort fields with bespoke ordering in `compareDeals`, kept for reference. */
export const SPECIAL_SORT_FIELDS = [
  "lastActivity",
  "name",
  "fitScore",
  "gapAnalysis",
  "added",
  "status",
] as const;

export type SortDirection = "asc" | "desc";

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface DealsViewStore {
  view: DealsView;
  sort: SortConfig;
  /**
   * Field ids from `components/deals/fields.ts`, in display order.
   *
   * `null` means "no choice made" and falls back to the registry's defaults, so
   * a user who has never touched the columns menu keeps getting new default
   * columns as they are added. An empty array is a real choice and is not the
   * same thing.
   *
   * Local for now. Stage 4 moves the column set onto saved views, which are
   * workspace-shared, and this becomes the per-user override of the active view.
   */
  visibleColumns: string[] | null;
  /**
   * AI column ids the user has switched off.
   *
   * A hidden-list rather than a visible-list, which is the opposite of
   * `visibleColumns` above and deliberately so. AI columns are user-defined and
   * created one at a time; somebody who has just defined one expects to see it,
   * and a visible-list would leave every new column off for everyone who had
   * ever opened the columns menu. Ids of deleted columns linger harmlessly —
   * the table only ever asks whether a column it is about to render is in here.
   */
  hiddenAiColumnIds: string[];
  setView: (view: DealsView) => void;
  setSort: (sort: SortConfig) => void;
  toggleSortDirection: () => void;
  setVisibleColumns: (columns: string[]) => void;
  toggleAiColumn: (columnId: string) => void;
  resetVisibleColumns: () => void;
}

const DEFAULT_SORT: SortConfig = { field: "lastActivity", direction: "desc" };

export const useDealsViewStore = create<DealsViewStore>()(
  persist(
    (set) => ({
      view: "table",
      sort: DEFAULT_SORT,
      visibleColumns: null,
      hiddenAiColumnIds: [],
      setView: (view) => set({ view }),
      setSort: (sort) => set({ sort }),
      toggleSortDirection: () =>
        set((state) => ({
          sort: {
            ...state.sort,
            direction: state.sort.direction === "asc" ? "desc" : "asc",
          },
        })),
      setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
      toggleAiColumn: (columnId) =>
        set((state) => ({
          hiddenAiColumnIds: state.hiddenAiColumnIds.includes(columnId)
            ? state.hiddenAiColumnIds.filter((id) => id !== columnId)
            : [...state.hiddenAiColumnIds, columnId],
        })),
      resetVisibleColumns: () =>
        set({ visibleColumns: null, hiddenAiColumnIds: [] }),
    }),
    {
      name: "deals-view-pref",
      version: 1,
      migrate: (persistedState: unknown, _version: number) => {
        if (persistedState && typeof persistedState === "object") {
          const state = persistedState as Partial<DealsViewStore>;
          if (!state.sort) {
            return { ...state, sort: DEFAULT_SORT };
          }
        }
        return persistedState;
      },
    }
  )
);
